import { Component, Inject, Input, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { AlarmGroup, alarmGroupNameMaxLimit, descriptionMaxLimit } from 'icreate/common/models/alarm-group';
import { ChannelCategory } from 'icreate/common/models/channel-category';
import { alarmGroupValidator } from 'icreate/common/validators/alarm-group/alarm-group-validator';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import { ListBubbleSelectorComponent } from '@components/list-bubble-selector/list-bubble-selector.component';
import { ChannelCategoryService } from '@service/channel/channel-category.service';

enum AlarmGroupInputName {
    NAME = 'name',
    DESCRIPTION = 'description',
}

@Component({
    selector: 'app-alarm-group-edit-dialog',
    templateUrl: './alarm-group-edit-dialog.component.html',
    styleUrls: ['./alarm-group-edit-dialog.component.css'],
    standalone: false,
})
export class AlarmGroupEditDialogComponent {
    readonly NAME_INPUT = AlarmGroupInputName.NAME;
    readonly DESCRIPTION_INPUT = AlarmGroupInputName.DESCRIPTION;
    readonly NAME_MAX_LENGTH = alarmGroupNameMaxLimit;
    readonly DESCRIPTION_MAX_LENGTH = descriptionMaxLimit;

    @Input() vesselId: string;
    @ViewChild('categoriesSelector') categoriesSelector: ListBubbleSelectorComponent;

    validationErrors: ValidationErrorMap = {};
    isLoading = true;

    groupName: string;
    groupDescription: string;

    channelCategories: ChannelCategory[] = [];
    selectedCategories: ChannelCategory[] = [];
    hasError: boolean;
    preSelectedCategories: ChannelCategory[];

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            id: string;
            name: string;
            description: string;
            vesselId: string;
            groupNo: number;
            alarmGroupList: AlarmGroup[];
            categories?: ChannelCategory[];
        },
        private dialogRef: MatDialogRef<AlarmGroupEditDialogComponent>,
        private channelCategoryService: ChannelCategoryService
    ) {}

    async ngOnInit() {
        this.vesselId = this.data.vesselId;
        this.channelCategories = await this.channelCategoryService.getList(this.vesselId);

        this.groupName = this.data.name ?? '';
        this.groupDescription = this.data.description ?? '';

        this.selectedCategories = this.channelCategories.filter(cc =>
            this.data.categories?.some(sc => sc.category === cc.category)
        );

        this.preSelectedCategories = [...this.selectedCategories];

        if (this.categoriesSelector && this.selectedCategories.length) {
            this.categoriesSelector.selectedElements = this.selectedCategories;
        }

        this.isLoading = false;
    }

    onInputChange(event: { name: string; value: string }) {
        if (event.name === this.NAME_INPUT) {
            this.groupName = event.value;
            this.checkInputErrors();
        }

        if (event.name === this.DESCRIPTION_INPUT) {
            this.groupDescription = event.value;
        }
    }

    onTextAreaChange(value) {
        this.groupDescription = value;
        this.checkInputErrors();
    }

    updateFilteredCategories(selectedCategories?: ChannelCategory[]) {
        if (selectedCategories) {
            this.selectedCategories = selectedCategories;
        }
    }

    get isEditDisabled() {
        const categoriesUnchanged =
            this.selectedCategories?.length === this.preSelectedCategories?.length &&
            this.selectedCategories?.every(sc => this.preSelectedCategories?.some(pc => pc.category === sc.category));

        return (
            this.hasError ||
            (this.groupName === this.data.name &&
                this.groupDescription === this.data.description &&
                categoriesUnchanged)
        );
    }

    editAlarmGroup() {
        this.dialogRef.close({
            alarmGroup: {
                name: this.groupName,
                description: this.groupDescription,
                categories: this.selectedCategories,
            },
            close: true,
        });
    }

    reset() {
        this.groupName = '';
        this.groupDescription = '';
        this.selectedCategories = [];
        this.categoriesSelector?.clearSelectedElements();
    }

    close() {
        this.dialogRef.close();
    }

    checkInputErrors() {
        const validationData = {
            name: this.groupName,
            description: this.groupDescription,
            categories: this.selectedCategories,
        };

        const errors = getValidationErrors(alarmGroupValidator(), validationData);
        const mappedErrors: { [key: string]: { message: string; params?: any } } = mapErrors(errors) || {};

        this.validationErrors = Object.keys(mappedErrors).length > 0 ? mappedErrors : null;

        this.hasError = !!(this.validationErrors?.name || this.validationErrors?.description);
    }

    get validatorFunction() {
        return alarmGroupValidator();
    }
}
